import gql from 'graphql-tag'

export const LocationFragment = gql`
  fragment LocationFragment on Location {
    id
    city
    state
    country
  }
`
export const LeaseNotificationsFragment = gql`
  fragment LeaseNotificationsFragment on Notification {
    id
    createdAt
    name
    description
    eventType
    data {
      ... on Contract {
        id
        createdAt
        name
        title
        noticeDate
        reminderDate
        effectiveDate
        endDate
        building {
          id
        }
        floor {
          id
        }
      }
    }
  }
`

export const FloorFragment = gql`
  fragment FloorFragment on Floor {
    id
    name
    code
    hidden
    story
    bookingWorkdayTimeFrom
    bookingWorkdayTimeTo
    notifications {
      edges {
        node {
          ...LeaseNotificationsFragment
        }
      }
    }
  }
  ${LeaseNotificationsFragment}
`

export const BuildingFragment = gql`
  fragment BuildingFragment on Building {
    id
    name
    code
    closedAt
    timezone
  }
`

export const FloorDetailsFragment = gql`
  fragment FloorDetailsFragment on Floor {
    ...FloorFragment
    building {
      ...BuildingFragment
      address
      city
      state
      closedAt
      region
    }
  }
  ${FloorFragment}
  ${BuildingFragment}
`

export const BuildingDetailsFragment = gql`
  fragment BuildingDetailsFragment on Building {
    ...BuildingFragment
    address
    city
    state
    timezone
    closedAt
    floors {
      ...FloorDetailsFragment
    }
  }
  ${BuildingFragment}
  ${FloorDetailsFragment}
`

export const SettingsFragment = gql`
  fragment SettingsFragment on CompanySettings {
    employeeImportNotificationEnabled
    hotelingDeskDeptEnforcementEnabled
    overrideBookingRestrictionEnabled
    enforceAuthAppEnabled
    unallocateSpaceOnUnseat
    hotelingBookingOverlapRestrictionEnabled
    hotelingDeskWpgEnforcementEnabled
    hotelingDeskWpgEnforcementOperator
    enablePolling
    spaceAvailabilityEnabled
    bookingAutoReleaseEnabled
    bookingCheckInRequired
    hourlyBookingEnabled
    dailyBookingEnabled
    ssoEnabled
    cc
    bookingWorkdayTimeFrom
    bookingWorkdayTimeTo
    enableHealthScreening
    healthScreeningMessage
    healthScreeningConfirmButtonText
    hoursAdminsGetHealthNoticeAfterEmployees
    hoursHealthMessageSentBeforeBooking
    enableBookingCapacityCap
    companyBookingCapacityCap
    enableLocationBookingCapacityCap
    viewPeerCurrentBookings
    showOtherBookers
    enableRecurringBookings
    maxFutureBookingDuration {
      ... on TimeDurationDays {
        days
      }
      ... on TimeDurationWeeks {
        weeks
      }
      ... on TimeDurationMonths {
        months
      }
    }
    autoReleaseGracePeriod
    healthNoticeEnabled
    bookingCheckInCancelEnabled
    locationHealthCheckDisabled {
      ...LocationFragment
    }
    hoursBeforeBookingCheckin
    employeeImportApprovalEnabled
    employeeImportApprovalThresholdPercent
    bookableSpaceTypes
    bookableSpaceTypesLocationsOverridesEnabled
    qrCodeEnabled
    bookingReminderEnabled
    bookingConfirmationEnabled
    bookingCancellationEnabled
    bookingNotesEnabled
    overrideSpaceTypeBookingRestrictionEnabled
    overriddenBookingRestrictionSpaceTypes
    bookingConfirmationOnbehalfEnabled
    bookingCancellationOnbehalfEnabled
    bookingCheckinReminderOnbehalfEnabled
  }
  ${LocationFragment}
`

export const TagFragment = gql`
  fragment TagFragment on Tag {
    id
    name
  }
`

export const AssetTagFragment = gql`
  fragment AssetTagFragment on Tag {
    id
    name
    totals {
      spaces
      employees
    }
  }
`

export const AssetTagEmployeeDetailsFragment = gql`
  fragment AssetTagEmployeeDetailsFragment on Employee {
    id
    name
    email
    assets {
      url(style: "thumbnail")
    }
    dept {
      name
    }
    team {
      name
    }
  }
`

export const AssignmentPlanFragment = gql`
  fragment AssignmentPlanFragment on AssignmentPlan {
    id
    parentAssignmentPlanId
    name
    alias
    apType
    deployAt
    createdAt
    createdBy {
      name
    }
  }
`

export const PageInfoFragment = gql`
  fragment PageInfoFragment on PageInfo {
    hasNextPage
    endCursor
  }
`

export const ImportAttributeFragment = gql`
  fragment ImportAttributeFragment on ImportAttribute {
    changed
    oldValue
    value
  }
`

export const ImportEmployeeFragment = gql`
  fragment ImportEmployeeFragment on Employee {
    externalId
    name
    firstName
    lastName
    email
    phone
    dept {
      name
    }
    reportingTo
    team {
      name
    }
    location
    title
    employmentType
    badge
  }
`
export const SpaceTypeFragment = gql`
  fragment SpaceTypeFragment on SpaceType {
    id
    name
    displayName
    interactivityType
    hidden
    interactivity
  }
`

export const UsageTypeFragment = gql`
  fragment UsageTypeFragment on UsageTypes {
    id
    name
    displayName
  }
`

export const SpaceFragment = gql`
  fragment SpaceFragment on Space {
    id
    code
    externalId
    name
    spaceType {
      ...SpaceTypeFragment
    }
    usageType {
      ...UsageTypeFragment
    }
  }
  ${SpaceTypeFragment}
  ${UsageTypeFragment}
`

export const EmployeeImportVersionFragment = gql`
  fragment EmployeeImportVersionFragment on EmployeeImportVersion {
    id
    event
    externalId {
      ...ImportAttributeFragment
    }
    name {
      ...ImportAttributeFragment
    }
    firstName {
      ...ImportAttributeFragment
    }
    lastName {
      ...ImportAttributeFragment
    }
    email {
      ...ImportAttributeFragment
    }
    phone {
      ...ImportAttributeFragment
    }
    departmentName {
      ...ImportAttributeFragment
    }
    reportingTo {
      ...ImportAttributeFragment
    }
    teamName {
      ...ImportAttributeFragment
    }
    location {
      ...ImportAttributeFragment
    }
    title {
      ...ImportAttributeFragment
    }
    employmentType {
      ...ImportAttributeFragment
    }
    badge {
      ...ImportAttributeFragment
    }

    employee {
      ...ImportEmployeeFragment
    }
  }
  ${ImportAttributeFragment}
  ${ImportEmployeeFragment}
`

export const EmployeeImportFailedFragment = gql`
  fragment EmployeeImportFailedFragment on EmployeeImportFailed {
    employee {
      externalId
      name
      firstName
      lastName
      email
      phone
      dept {
        name
      }
      reportingTo
      team {
        name
      }
      location
      title
      employmentType
    }
    errors {
      externalId
      name
      firstName
      lastName
      email
      phone
      reportingTo
      location
      title
      employmentType
      departmentId
      badge
    }
  }
`

export const ScenarioGroupFragment = gql`
  fragment ScenarioGroupFragment on ScenarioGroup {
    id
    deployAt
    assignmentPlans {
      ...AssignmentPlanFragment
    }
  }
  ${AssignmentPlanFragment}
`

const EmployeeSpaceAssignmentFragment = gql`
  fragment EmployeeSpaceAssignmentFragment on Space {
    id
    externalId
    floor {
      id
    }
    x
    y
    w
  }
`

export const NeighborhoodSearchFragment = gql`
  fragment NeighborhoodSearchFragment on NeighborhoodSearch {
    id
    name
    color
    floor {
      id
    }
  }
`

export const DepartmentFragment = gql`
  fragment DepartmentFragment on Department {
    id
    name
    color
    parentDepartment {
      id
      name
      color
    }
    totals {
      teams
    }
  }
`

export const EmployeeFragment = gql`
  fragment EmployeeFragment on Employee {
    id
    title
    name
    email
    role
    spaceAssignment {
      space {
        id
        code
        externalId
        floor {
          id
        }
      }
    }
    assets {
      url(style: "thumbnail")
    }
    dept {
      id
      name
    }
    team {
      id
      name
    }
  }
`

export const DMTableDepartmentFragment = gql`
  fragment DMTableDepartmentFragment on Department {
    ...DepartmentFragment
    colorUpdatedAt
    updatedBy {
      employee {
        ...EmployeeFragment
      }
    }
    totals {
      employees
      spaces
      teams
    }
  }
  ${DepartmentFragment}
  ${EmployeeFragment}
`

export const DMDialogSpaceFragment = gql`
  fragment DMDialogSpaceFragment on Space {
    ...SpaceFragment
    spaceAssignment {
      department {
        id
        name
      }
      team {
        id
        name
      }
    }
  }
  ${SpaceFragment}
`

export const NeighborhoodEmployeeFragment = gql`
  fragment NeighborhoodEmployeeFragment on Employee {
    id
    name
    email
    dept {
      ...DepartmentFragment
    }
    assets {
      url(style: "thumbnail")
    }
  }
  ${DepartmentFragment}
`

export const EmployeeSearchFragment = gql`
  fragment EmployeeSearchFragment on EmployeeSearch {
    id
    name
    assets {
      url(style: "thumbnail")
    }
  }
`

export const WorkplaceGroupSearchFragment = gql`
  fragment WorkplaceGroupSearchFragment on WorkplaceGroup {
    id
    name
    color
  }
`

export const NeighborhoodFragment = gql`
  fragment NeighborhoodFragment on Neighborhood {
    id
    name
    color
    employees {
      dept {
        id
        name
      }
      id
      name
    }
    floor {
      id
      name
      building {
        name
        city
      }
    }
  }
`

export const WorkplaceGroupFragment = gql`
  fragment WorkplaceGroupFragment on WorkplaceGroup {
    id
    name
    color
  }
`

export const NeighborhoodSpaceFragment = gql`
  fragment NeighborhoodSpaceFragment on Space {
    id
    code
    spaceAssignment {
      department {
        name
      }
    }
  }
`

export const WpgSpaceFragment = gql`
  fragment WpgSpaceFragment on Space {
    id
    code
    spaceAssignment {
      department {
        name
      }
    }
  }
`

export const NeighborhoodTableRecordFragment = gql`
  fragment NeighborhoodTableRecordFragment on Neighborhood {
    ...NeighborhoodFragment
    counters {
      employees
      workstations
      occupancy
      maxEmployees
      sharingRatio
    }
    createdBy {
      employee {
        ...EmployeeFragment
      }
    }
    createdAt
  }
  ${NeighborhoodFragment}
  ${EmployeeFragment}
`

export const WorkplaceGroupTableRecordFragment = gql`
  fragment WorkplaceGroupTableRecordFragment on WorkplaceGroup {
    ...WorkplaceGroupFragment
    totals {
      spaces
      employees
    }
    createdBy {
      employee {
        ...EmployeeFragment
      }
    }
    createdAt
  }
  ${WorkplaceGroupFragment}
  ${EmployeeFragment}
`

export const WorkplaceGroupDetails = gql`
  fragment WorkplaceGroupDetails on WorkplaceGroup {
    ...WorkplaceGroupFragment
    spaces {
      counters {
        total
      }
    }
    employees {
      counters {
        total
      }
    }
    createdBy {
      employee {
        ...EmployeeFragment
      }
    }
    createdAt
  }
  ${EmployeeFragment}
  ${WorkplaceGroupFragment}
`

export const WorkplaceGroupEmployees = gql`
  fragment WorkplaceGroupEmployees on WorkplaceGroup {
    employees(where: $where, first: $first, after: $after, orderBy: name_ASC) {
      counters {
        total
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          ...EmployeeFragment
        }
      }
    }
  }
  ${EmployeeFragment}
`

export const WorkplaceGroupSpaces = gql`
  fragment WorkplaceGroupSpaces on WorkplaceGroup {
    spaces(where: $where, first: $first, after: $after) {
      counters {
        total
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          id
          name
          externalId
          usageType {
            ...UsageTypeFragment
          }
          spaceType {
            ...SpaceTypeFragment
          }
          spaceAssignment {
            department {
              name
            }
            team {
              name
            }
          }
        }
      }
    }
  }
  ${UsageTypeFragment}
  ${SpaceTypeFragment}
`

export const NeighborhoodEmployees = gql`
  fragment NeighborhoodEmployees on Neighborhood {
    employeeConnection(where: $where, first: $first, after: $after, orderBy: name_ASC) {
      counters {
        total
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          ...EmployeeFragment
        }
      }
    }
  }
  ${EmployeeFragment}
`

export const NeighborhoodSpaces = gql`
  fragment NeighborhoodSpaces on Neighborhood {
    spaceConnection(where: $where, first: $first, after: $after) {
      counters {
        total
      }
      pageInfo {
        endCursor
        hasNextPage
      }
      edges {
        node {
          id
          name
          code
          externalId
          usageType {
            ...UsageTypeFragment
          }
          spaceType {
            ...SpaceTypeFragment
          }
          spaceAssignment {
            department {
              name
            }
            team {
              name
            }
          }
        }
      }
    }
  }
  ${UsageTypeFragment}
  ${SpaceTypeFragment}
`

export const NeighborhoodDetailsFragment = gql`
  fragment NeighborhoodDetailsFragment on Neighborhood {
    ...NeighborhoodTableRecordFragment
    owner {
      id
      employee {
        ...EmployeeFragment
      }
    }
    createdBy {
      employee {
        ...EmployeeFragment
      }
    }
    createdAt
  }
  ${NeighborhoodTableRecordFragment}
  ${EmployeeFragment}
`

export const EmployeeTableRecordFragment = gql`
  fragment EmployeeTableRecordFragment on Employee {
    id
    name
    title
    imported
    email
    employmentType
    isRemote
    noDeskNeeded
    assets {
      url(style: "thumbnail")
    }
    dept {
      ...DepartmentFragment
    }
    team {
      ...DepartmentFragment
    }
    manager {
      id
      name
    }
    geoLocation {
      id
      location
    }
    spaceAssignment(assignmentPlanId: $assignmentPlanId) {
      space {
        ...EmployeeSpaceAssignmentFragment
      }
    }
    secondarySpaceAssignments {
      space {
        ...EmployeeSpaceAssignmentFragment
      }
    }
    neighborhoods {
      ...NeighborhoodFragment
    }
  }
  ${DepartmentFragment}
  ${NeighborhoodFragment}
  ${EmployeeSpaceAssignmentFragment}
`

export const BookingEmployeeFragment = gql`
  fragment BookingEmployeeFragment on Employee {
    id
    name
    email
    assets {
      url(style: "thumbnail")
    }
    title
    department {
      id
    }
  }
`

export const BookingSpaceFragment = gql`
  fragment BookingSpaceFragment on Space {
    externalId
    usageType {
      ...UsageTypeFragment
    }
    floor {
      id
      name
      building {
        id
        name
        city
        timezone
      }
      printableQrCodeUsageTypes
      enforceQrCode
    }
  }
  ${UsageTypeFragment}
`

export const BookingScheduleFragment = gql`
  fragment BookingScheduleFragment on BookingSchedule {
    id
    recurrencePattern
    effectiveEndDate
    effectiveStartDate
    pattern {
      dayOfMonth
      daysOfWeek
      interval
      type
    }
    range {
      endDate
      numberOfOccurrences
      startDate
      type
    }
  }
`

export const BookingFragment = gql`
  fragment BookingFragment on Booking {
    id
    startDate
    endDate
    employee {
      ...BookingEmployeeFragment
    }
    bookedBy {
      employee {
        ...BookingEmployeeFragment
      }
    }
    bookingSchedule {
      ...BookingScheduleFragment
    }
    space {
      ...BookingSpaceFragment
    }
    passedHealthCheck
    checkedIn
    healthCheck {
      disabled
      locationHeathCheckGroup {
        id
      }
    }
  }
  ${BookingEmployeeFragment}
  ${BookingSpaceFragment}
  ${BookingScheduleFragment}
`

export const BookingImportFailedFragment = gql`
  fragment BookingImportFailedFragment on BookingImportFailed {
    booking {
      ...BookingFragment
    }
    errors {
      base
      autoReleasedAt
      checkedIn
      checkinEmailSentAt
      createdById
      deletedAt
      employeeId
      endDate
      id
      spaceId
      startDate
    }
  }
  ${BookingFragment}
`

export const EmployeeConnectionWithTotalFragment = gql`
  fragment EmployeeConnectionWithTotalFragment on Employee {
    id
    name
    title
    imported
    email
    employmentType
    isRemote
    noDeskNeeded
    assets {
      url(style: "thumbnail")
    }
    dept {
      ...DepartmentFragment
    }
    team {
      ...DepartmentFragment
    }
    manager {
      id
      name
    }
    geoLocation {
      id
      location
    }
    spaceAssignment(assignmentPlanId: $assignmentPlanId) {
      space {
        ...EmployeeSpaceAssignmentFragment
      }
    }
    secondarySpaceAssignments {
      space {
        ...EmployeeSpaceAssignmentFragment
      }
    }
    neighborhoods {
      ...NeighborhoodFragment
    }
  }
  ${DepartmentFragment}
  ${NeighborhoodFragment}
  ${EmployeeSpaceAssignmentFragment}
`

export const EmployeeDetailsFragment = gql`
  fragment EmployeeDetailsFragment on Employee {
    ...EmployeeTableRecordFragment
    role
    user {
      bookingManagerSetting: bookingsManagerSetting {
        managed_employees_ids
        managed_employees {
          id
          name
          email
        }
        managed_departments_ids
        managed_departments {
          id
          name
          color
          colors
          totals {
            teams
          }
          parentDepartment {
            id
            name
            color
          }
          employeeConnection {
            edges {
              node {
                name
                id
                email
              }
            }
          }
        }
        manage_all
      }
    }
    phone
    badge
    startDate
    endDate
    note
    extAttributes
    externalId
    slackHandle
    costCenter {
      name
    }
    user {
      bookingManagerSetting: bookingsManagerSetting {
        manage_all
      }
    }
  }
  ${EmployeeTableRecordFragment}
`

export const CatalogEmployeeFragment = gql`
  fragment Employee on Employee {
    id
    name
    email
    phone
    note
    isRemote
    noDeskNeeded
    assets {
      url(style: "thumbnail")
    }
    dept {
      ...DepartmentFragment
    }
    team {
      ...DepartmentFragment
    }
    spaceAssignment(assignmentPlanId: $assignmentPlanId) {
      space {
        ...EmployeeSpaceAssignmentFragment
      }
    }
    secondarySpaceAssignments {
      space {
        ...EmployeeSpaceAssignmentFragment
      }
    }
    neighborhoods {
      ...NeighborhoodFragment
    }
  }
  ${DepartmentFragment}
  ${EmployeeSpaceAssignmentFragment}
  ${NeighborhoodFragment}
`

export const EmployeeImportFragment = gql`
  fragment EmployeeImportFragment on EmployeeImport {
    id
    createdAt
    status
    source
    totalCount
    createdCount
    destroyedCount
    seatedCount
    updatedCount
    failedCount
    importedBy
    url
    backupUrl
  }
`

export const DashboardEmployeeImportFragment = gql`
  fragment DashboardEmployeeImportFragment on DashboardEmployeeImport {
    id
    createdAt
    status
    source
    totalCount
    createdCount
    destroyedCount
    seatedCount
    updatedCount
    failedCount
    importedBy
    autoCancelAt
  }
`

export const BookingImportFragment = gql`
  fragment BookingImportFragment on BookingImport {
    id
    createdAt
    status
    source
    totalCount
    createdCount
    failedCount
    importedBy
    url
  }
`

export const BookingImportVersionFragment = gql`
  fragment BookingImportVersionFragment on BookingImportVersion {
    id
    event
    booking {
      ...BookingFragment
    }
  }
  ${BookingFragment}
`

export const NeighborhoodImportFragment = gql`
  fragment NeighborhoodImportFragment on EmployeesNeighborhoodsImport {
    id
    createdAt
    status
    source
    totalCount
    createdCount
    failedCount
    importedBy
    url
  }
`

export const EmployeeNeighborhoodFragment = gql`
  fragment EmployeeNeighborhoodFragment on EmployeeNeighborhood {
    employee {
      name
      email
      department {
        name
      }
    }
    id
    neighborhood {
      name
      floor {
        name
        building {
          name
          city
        }
      }
    }
  }
`

export const NeighborhoodImportVersionFragment = gql`
  fragment NeighborhoodImportVersionFragment on EmployeesNeighborhoodImportVersion {
    id
    event
    employee_neighborhood {
      ...EmployeeNeighborhoodFragment
    }
  }
  ${EmployeeNeighborhoodFragment}
`

export const NeighborhoodImportFailedFragment = gql`
  fragment NeighborhoodImportFailedFragment on EmployeesNeighborhoodsImportFailed {
    errors {
      base
      createdAt
      employeeId
      id
      updatedAt
    }
  }
`

export const ImportProgressFragment = gql`
  fragment ImportProgressFragment on ImportProgress {
    progress
    total
  }
`

export const FloorPlanFragment = gql`
  fragment FloorPlanFragment on Floor {
    id
    name
    building {
      id
      name
      address
      region
    }
  }
`

export const ManagedDepartmentFragment = gql`
  fragment ManagedDepartmentFragment on Department {
    id
    name
    color
    colors
  }
`

export const AssetFragment = gql`
  fragment AssetFragment on File {
    id
    createdAt
    updatedAt
    name
    url
  }
`

export const MeridianMapFragment = gql`
  fragment MeridianMapFragment on IntegrationFloor {
    externalId
  }
`

export const BMTableFloorFragment = gql`
  fragment BMTableFloorFragment on Floor {
    id
    name
    story
    code
    capacity
    areaSize
    usableArea
    rentableArea
    url
    hidden
    safeDistance
    assetVersion {
      background {
        url(style: "half")
        updatedAt
      }
    }
  }
`

export const BMTableBuildingFragment = gql`
  fragment BMTableBuildingFragment on Building {
    id
    name
    city
    address
    state
    closedAt
    code
    geoLocation {
      id
      location
    }
    floors {
      ...BMTableFloorFragment
    }
    buildingType {
      id
      displayName
      name
    }
  }
  ${BMTableFloorFragment}
`

export const BMDialogBuildingFragment = gql`
  fragment BMDialogBuildingFragment on Building {
    id
    name
    address
    code
    closedAt
    buildingType {
      id
      name
      displayName
    }
    geoLocation {
      id
      location
    }
    meridianLocation {
      externalId
    }
    floors {
      name
      id
    }
  }
`

export const BMDialogFloorFragment = gql`
  fragment BMDialogFloorFragment on Floor {
    id
    building {
      id
      code
    }
    enforceQrCode
    printableQrCodeUsageTypes
    name
    story
    code
    areaSize
    usableArea
    rentableArea
    capacity
    hidden
    scale
    safeDistance
    assetVersion {
      id
      background {
        ...AssetFragment
      }
      autocad {
        ...AssetFragment
      }
      mapW
      mapH
    }
    meridianMap {
      ...MeridianMapFragment
    }
  }
  ${AssetFragment}
  ${MeridianMapFragment}
`

export const SpaceTagFragment = gql`
  fragment SpaceTagFragment on TagSearch {
    id
    name
  }
`

export const CostCenterFragment = gql`
  fragment CostCenterFragment on CostCenter {
    id
    name
  }
`

export const BuildingTypeFragment = gql`
  fragment BuildingTypeFragment on BuildingType {
    id
    name
    displayName
  }
`

export const DepartmentSearchFragment = gql`
  fragment DepartmentSearchFragment on DepartmentSearch {
    id
    name
    color
    colors
  }
`

export const GlobalMapObjectFragment = gql`
  fragment GlobalMapObjectFragment on GlobalMapObject {
    id
    name
    color
    name
    icon
    fontFamily
    limitOnePerFloor
  }
`

export const contractEventAssetFragment = gql`
  fragment ContractEventAssetFragment on ContractEventAsset {
    id
    fileName
    url
    createdAt
    updatedAt
    contentType
    fileSize
  }
`

export const contractEventFragment = gql`
  fragment ContractEventFragment on ContractEvent {
    id
    amount
    createdAt
    createdBy {
      name
    }
    date
    description
    dateAlert
    eventType
    units
    files {
      ...ContractEventAssetFragment
    }
  }
  ${contractEventAssetFragment}
`

export const LeaseFragment = gql`
  fragment LeaseFragment on Contract {
    id
    floor {
      id
      name
      building {
        id
        city
        address
        name
      }
    }
    building {
      id
      city
      address
      name
    }
    contactName
    contactInfo
    endDate
    endAlert
    noticeDate
    noticeAlert
    effectiveDate
    effectiveAlert
    reminderDate
    reminderAlert
    startDate
    name
    title
    cost
    monthlyCost
    totalCost
    costPerSqft
    metadata
    owner
    notes
    employees {
      ...EmployeeFragment
    }
    contractEvents {
      ...ContractEventFragment
    }
    files {
      id
      fileName
      contentType
      url
      createdAt
    }
  }
  ${contractEventFragment}
  ${EmployeeFragment}
`

export const AssetTagSpaceDetailsFragment = gql`
  fragment AssetTagSpaceDetailsFragment on Space {
    ...SpaceFragment
    spaceAssignment {
      department {
        name
      }
      team {
        name
      }
    }
  }
  ${SpaceFragment}
  ${UsageTypeFragment}
  ${SpaceTypeFragment}
`

export const HRLocationFragment = gql`
  fragment HRLocationFragment on GeoLocation {
    id
    location
  }
`

export const ExportStatusFragment = gql`
  fragment ExportStatusFragment on EmployeeExportGraph {
    progress
    total
    url
  }
`

export const ExportFragment = gql`
  fragment ExportFragment on EmployeeExportGraph {
    id
    url
  }
`

export const TicketFragment = gql`
  fragment TicketFragment on Ticket {
    id
    ticketNumber
    source
    externalUrl
    createdDate
    dueDate
    shortDescription
    severity
    status
    employee {
      ...EmployeeFragment
    }
    assignedTo {
      ...EmployeeFragment
    }
  }
  ${EmployeeFragment}
`

export const TicketDetailsFragment = gql`
  fragment TicketDetailsFragment on Ticket {
    ...TicketFragment
    description
    building {
      ...BuildingFragment
    }
    floor_plan {
      ...FloorFragment
    }
  }
  ${TicketFragment}
  ${BuildingFragment}
  ${FloorFragment}
`

export const TicketSearchResultFragment = gql`
  fragment TicketSearchResultFragment on Ticket {
    id
    ticketNumber
    shortDescription
    status
    assignedTo {
      id
      name
    }
  }
`

export const FloorMapBlockFragment = gql`
  fragment FloorMapBlockFragment on Block {
    id
    url
  }
`

export const FloorMapBlockInstanceFragment = gql`
  fragment FloorMapBlockInstanceFragment on BlockInstance {
    id
    block {
      ...FloorMapBlockFragment
    }
    map {
      x
      y
      w
      h
      rotation
    }
  }
  ${FloorMapBlockFragment}
`

export const FloorAssetsVersionFragment = gql`
  fragment FloorAssetsVersionFragment on FloorAssetsVersion {
    id
    mapW
    mapH
    background {
      url
    }
    blocks {
      ...FloorMapBlockFragment
    }
    blockInstances {
      ...FloorMapBlockInstanceFragment
    }
  }
  ${FloorMapBlockFragment}
  ${FloorMapBlockInstanceFragment}
`

export const FloorMapEmployeeFragment = gql`
  fragment FloorMapEmployeeFragment on Employee {
    ...EmployeeFragment
    dept {
      ...DepartmentFragment
    }
    team {
      ...DepartmentFragment
    }
    location
    geoLocation {
      location
    }
    email
    phone
    note
    startDate
    endDate
    badge
    externalId
    employmentType
    costCenter {
      id
      name
    }
    manager {
      id
      name
    }
    role
    isRemote
    noDeskNeeded
    tags {
      id
      name
    }
    workplaceGroups {
      id
      name
    }
    neighborhoods {
      id
      name
    }
  }
  ${EmployeeFragment}
  ${DepartmentFragment}
`

export const FloorMapFragment = gql`
  fragment FloorMapFragment on Floor {
    id
    name
    scale
    safeDistance
    capacity
    hidden
    building {
      name
      timezone
      geoLocation {
        id
      }
    }
    assetVersion {
      ...FloorAssetsVersionFragment
    }
  }
  ${FloorAssetsVersionFragment}
`

export const SpaceAssignmentFragment = gql`
  fragment SpaceAssignmentFragment on SpaceAssignment {
    employee {
      ...FloorMapEmployeeFragment
    }
    sharedEmployees {
      ...FloorMapEmployeeFragment
    }
    department {
      ...DepartmentFragment
    }
    team {
      ...DepartmentFragment
    }
    neighborhood {
      ...NeighborhoodFragment
    }
  }
  ${FloorMapEmployeeFragment}
  ${DepartmentFragment}
  ${NeighborhoodFragment}
`

export const CompanyAssetFragment = gql`
  fragment CompanyAssetFragment on CompanyAsset {
    fileName
    id
    updatedAt
    url
  }
`

export const FloorMapSpaceFragment = gql`
  fragment FloorMapSpaceFragment on Space {
    id
    w
    x
    y
    code
    externalId
    name
    comment
    density
    status
    capacity
    measurements
    assets {
      id
      url
      fileName
      updatedAt
      isDefault
    }
    spaceType {
      ...SpaceTypeFragment
    }
    usageType {
      ...UsageTypeFragment
    }
    workplaceGroup {
      id
      name
      color
    }
    workplaceGroups {
      id
      name
      color
    }
    tags {
      id
      name
    }
  }
  ${SpaceTypeFragment}
  ${UsageTypeFragment}
`

export const FloorMapPinFragment = gql`
  fragment FloorMapPinFragment on Pin {
    id
    map {
      x
      y
      w
      h
      name
    }
    globalMapObject {
      ...GlobalMapObjectFragment
    }
  }
  ${GlobalMapObjectFragment}
`

export const SpaceDetailsFragment = gql`
  fragment SpaceDetailsFragment on Space {
    capacity
    measurements
    floor {
      id
      name
    }
    workplaceGroup {
      id
      color
      name
    }
    department {
      id
      color
      name
    }
    tags {
      ...TagFragment
    }
    ...FloorMapSpaceFragment
    spaceAssignment(assignmentPlanId: $assignmentPlanId) {
      ...SpaceAssignmentFragment
    }
    calendar {
      id
      calendarName
    }
  }
  ${FloorMapSpaceFragment}
  ${SpaceAssignmentFragment}
  ${TagFragment}
`

export const MoveOrderSpaceFragment = gql`
  fragment MoveOrderSpaceFragment on Space {
    id
    code
    externalId
    spaceType {
      ...SpaceTypeFragment
    }
    usageType {
      ...UsageTypeFragment
    }
    spaceAssignment(assignmentPlanId: $assignmentPlanId) {
      ...SpaceAssignmentFragment
    }
  }
  ${SpaceTypeFragment}
  ${UsageTypeFragment}
  ${SpaceAssignmentFragment}
`

export const MoveOrderFragment = gql`
  fragment MoveOrderFragment on MoveOrder {
    id
    fromSpace {
      ...MoveOrderSpaceFragment
    }
    toSpace {
      ...MoveOrderSpaceFragment
    }
  }
  ${MoveOrderSpaceFragment}
`

export const DepartmentPortfolioFragment = gql`
  fragment DepartmentPortfolioFragment on DepartmentPortfolio {
    allocated
    areaSqft
    areaSqrm
    occupied
    unoccupied
    seatedCount
    department {
      id
      name
      color
    }
    teams {
      allocated
      areaSqft
      areaSqrm
      occupied
      unoccupied
      department {
        id
        name
        color
      }
    }
  }
`
export const SpacePortfolioFragment = gql`
  fragment SpacePortfolioFragment on SpacePortfolio {
    areaSqft
    areaSqrm
    readableType
    spaceType
    workstations
  }
`
export const SpaceUsageTypePortfolioFragment = gql`
  fragment SpaceUsageTypePortfolioFragment on SpaceUsageTypePortfolio {
    allocated
    areaSqft
    areaSqrm
    floorArea
    occupancy
    occupied
    readableType
    spaceUsageType
    unoccupancy
    unoccupied
    workstations
  }
`

export const PortfolioBuildingFloorsFragment = gql`
  fragment PortfolioBuildingFloorsFragment on BuildingPortfolio {
    floors {
      areaSqft
      areaSqrm
      densitySqft
      densitySqrm
      occupancy
      allocated
      unallocated
      unoccupied
      occupied
      workstations
      seatedCount
      floor {
        id
        name
        code
        areaSize
        story
        scale
        hidden
        capacity
        building {
          id
        }
        map {
          url
          fileName
          updatedAt
        }
        assetVersion {
          id
          mapW
          mapH
          autocad {
            url
          }
          background {
            url
          }
        }
        meridianMap {
          externalId
        }
        contracts {
          id
          title
          cost
          endDate
        }
        notifications {
          edges {
            node {
              ...LeaseNotificationsFragment
            }
          }
        }
      }
      spaceTypes {
        ...SpacePortfolioFragment
      }
      spaceUsageTypes {
        ...SpaceUsageTypePortfolioFragment
      }
      departments {
        ...DepartmentPortfolioFragment
      }
    }
  }
  ${DepartmentPortfolioFragment}
  ${SpacePortfolioFragment}
  ${SpaceUsageTypePortfolioFragment}
  ${LeaseNotificationsFragment}
`

export const PortfolioBuildingFragment = gql`
  fragment PortfolioBuildingFragment on BuildingPortfolio {
    areaSqft
    areaSqrm
    densitySqft
    densitySqrm
    occupancy
    unallocated
    unoccupied
    occupied
    workstations
    seatedCount
    allocated
    spaceTypes {
      ...SpacePortfolioFragment
    }
    spaceUsageTypes {
      ...SpaceUsageTypePortfolioFragment
    }
    departments {
      ...DepartmentPortfolioFragment
    }
    building {
      id
      name
      code
      address
      leaseAnnualCost
      closedAt
      notifications {
        edges {
          node {
            ...LeaseNotificationsFragment
          }
        }
      }
      meridianLocation {
        externalId
      }
      leases {
        id
        title
        cost
        endDate
      }
      city
      lat
      long
      contact {
        name
        phone
      }
      geoLocation {
        id
        location
      }
    }
    ...PortfolioBuildingFloorsFragment
  }
  ${PortfolioBuildingFloorsFragment}
  ${DepartmentPortfolioFragment}
  ${SpacePortfolioFragment}
  ${SpaceUsageTypePortfolioFragment}
  ${LeaseNotificationsFragment}
`

export const PortfolioTooltipBuildingFragment = gql`
  fragment PortfolioTooltipBuildingFragment on BuildingPortfolio {
    workstations
    unoccupied
    departments {
      allocated
      occupied
      unoccupied
      seatedCount
      areaSqft
      areaSqrm
      department {
        color
        name
      }
    }
    spaceUsageTypes {
      areaSqft
      areaSqrm
      readableType
      spaceUsageType
      workstations
    }
    floors {
      floor {
        id
        name
        building {
          id
        }
      }
      departments {
        areaSqft
        areaSqrm
        allocated
        occupied
        unoccupied
        seatedCount
        department {
          name
          color
        }
      }
      spaceUsageTypes {
        areaSqft
        areaSqrm
        readableType
        spaceUsageType
        workstations
      }
    }
  }
`

export const LocationCapacityFragment = gql`
  fragment LocationCapacityFragment on CapacityLocations {
    capacity
    locations {
      ...LocationFragment
    }
  }
  ${LocationFragment}
`

export const LocationHealthCheckFragment = gql`
  fragment LocationHealthCheckFragment on LocationHealthCheckGroup {
    id
    healthScreeningConfirmButtonText
    healthScreeningMessage
    followUpQuestionEnabled
    followUpQuestionRecipient
    followUpQuestionText
    healthScreeningOverride
    locations {
      ...LocationFragment
    }
  }
  ${LocationFragment}
`
